/**
 * AccordionItem component.
 * Individual accordion item with header actions support.
 * @module components/ui/Accordion/AccordionItem
 */

import { useEffect, useId } from "react";

import type { ElementType, KeyboardEvent, MouseEvent, ReactNode } from "react";

import { Button, type ButtonProps } from "@/components/ui/Button";
import { useConfirm } from "@/components/ui/ConfirmProvider";
import { Icon } from "@/components/ui/Icon";

import { useAccordion } from "./context";

export interface AccordionItemProps {
  /** Unique key for this item */
  eventKey: string;
  /** Title displayed in the header */
  title: ReactNode;
  /** Optional subtitle displayed below the title or on the right */
  subtitle?: ReactNode;
  /** Position of the subtitle: 'below' (default) or 'right' */
  subtitlePosition?: "below" | "right";
  /** Item content */
  children: ReactNode;
  /** Optional actions to render in the header (use HeaderAction) */
  headerActions?: ReactNode;
  /** A control that belongs with the title, such as a select-all checkbox, before the chevron. */
  headerLeading?: ReactNode;
  /** When true, the header sticks to the top of the scroll container while the item is expanded */
  stickyHeader?: boolean;
  /**
   * Document level of the header. An accordion under a section heading is
   * below it in the outline, not beside it, and a reader navigating by
   * heading is what the level is for.
   */
  headingLevel?: 2 | 3 | 4 | 5;
  /** Additional CSS classes for the item */
  className?: string;
  /** Test ID for the item */
  "data-testid"?: string;
}

/**
 * AccordionItem component with support for header actions.
 */
export function AccordionItem({
  eventKey,
  title,
  subtitle = undefined,
  subtitlePosition = "below",
  children,
  headerActions = undefined,
  headerLeading = undefined,
  stickyHeader = false,
  headingLevel = 2,
  className = "",
  "data-testid": testId,
}: AccordionItemProps) {
  const { expandedKeys, toggleItem, registerItem } = useAccordion();
  const isExpanded = expandedKeys.has(eventKey);

  // Register this item with the parent accordion
  useEffect(() => registerItem(eventKey), [eventKey, registerItem]);

  const handleToggle = () => {
    toggleItem(eventKey);
  };

  const handleHeaderKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleItem(eventKey);
    }
  };

  // `aria-controls` is a space-separated id-reference list, so a key holding
  // a phrase would name several elements rather than one. Every run of
  // non-id characters collapses to a single hyphen, which keeps two distinct
  // keys distinct for anything that differs by more than its punctuation.
  // `useId` is appended so two accordions that reuse a key never share ids.
  const instance = useId().replace(/[^\w-]/g, "");
  const domKey = `${eventKey.replace(/[^\w.:-]+/g, "-")}-${instance}`;
  const headingId = `${domKey}_heading`;
  const collapseId = `${domKey}_collapse`;
  const HeadingTag = `h${headingLevel}` as ElementType;

  return (
    <div
      className={`join-item border border-base-300 bg-base-100${className ? ` ${className}` : ""}`}
      data-testid={testId}
    >
      <HeadingTag
        id={headingId}
        className={
          stickyHeader ? "bg-base-100 lg:sticky lg:top-(--sq-topbar-height) lg:z-20" : undefined
        }
      >
        <div className="flex items-stretch bg-base-200/40 transition-colors hover:bg-base-200 max-sm:flex-col">
          {/* Leading control and title stay on one row when the actions stack. */}
          <div className="flex min-w-0 grow items-stretch">
            {headerLeading && <div className="flex items-center pl-3">{headerLeading}</div>}
            <button
              className="grow min-w-0 flex items-center gap-2 p-3 text-left w-full"
              type="button"
              aria-expanded={isExpanded}
              aria-controls={collapseId}
              onClick={handleToggle}
              onKeyDown={handleHeaderKeyDown}
              data-testid={testId ? `${testId}-header` : undefined}
            >
              <Icon
                name="caret-down"
                className={`accordion-chevron text-sm${isExpanded ? "" : " -rotate-90"}`}
                aria-hidden
              />
              <div
                className={
                  subtitlePosition === "right" ? "flex w-full items-center justify-between" : "grow"
                }
              >
                <div className="font-semibold min-w-0">{title}</div>
                {subtitle && subtitlePosition === "below" && (
                  <div className="text-sm text-muted">{subtitle}</div>
                )}
                {subtitle && subtitlePosition === "right" && (
                  <div className="text-sm text-muted mr-2">{subtitle}</div>
                )}
              </div>
            </button>
          </div>
          {headerActions && (
            /* Below sm the group may wrap: badges and buttons set
               white-space: nowrap, so unwrapped their sum would push the
               header past a narrow viewport. From sm up it stays on one line,
               so a help icon never drops under the badge beside it. */
            <div className="accordion-header-actions flex flex-wrap items-center justify-end gap-2 px-2 mr-2 sm:flex-nowrap max-sm:mr-0 max-sm:justify-start max-sm:px-3 max-sm:pb-3">
              {headerActions}
            </div>
          )}
        </div>
      </HeadingTag>
      <div id={collapseId}>
        {isExpanded && <div className="p-2 sm:p-4 border-t border-base-300">{children}</div>}
      </div>
    </div>
  );
}

/**
 * HeaderAction component for use inside accordion headers.
 */
export interface HeaderActionProps {
  /** Click handler */
  onClick: () => void;
  /** Icon name */
  icon?: string;
  /** Button label */
  label?: string;
  /** Accessible label for screen readers */
  "aria-label"?: string;
  /** Button variant */
  variant?:
    "primary" | "secondary" | "danger" | "outline-primary" | "outline-secondary" | "outline-danger";
  /** Test ID */
  "data-testid"?: string;
  /** Additional class name */
  className?: string;
  /** Confirmation heading for destructive actions. */
  confirmationTitle?: string;
  /** Confirmation detail for destructive actions. */
  confirmationMessage?: string;
  /** Destructive confirmation button label. */
  confirmLabel?: string;
  /** Refuses the press but stays focusable; see {@link Button}. */
  disabled?: boolean;
  /** Why it is refused: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  /** Additional data attributes for E2E testing */
  [key: `data-${string}`]: string | undefined;
}

/**
 * The older variant names, on the shared button. A danger action is outlined
 * rather than filled, the same emphasis `Button` gives every destructive press.
 */
function variantToButtonProps(
  variant: HeaderActionProps["variant"] = "outline-secondary",
): Pick<ButtonProps, "variant" | "tone"> {
  switch (variant) {
    case "primary":
      return { variant: "primary" };
    case "secondary":
      return { variant: "ghost" };
    case "danger":
    case "outline-danger":
      return { variant: "outline", tone: "danger" };
    default:
      return { variant: "outline" };
  }
}

/**
 * Header action for accordion items.
 */
export function HeaderAction({
  onClick,
  icon = undefined,
  label = undefined,
  "aria-label": ariaLabel,
  variant = "outline-secondary",
  "data-testid": testId,
  className = "",
  confirmationTitle = undefined,
  confirmationMessage = undefined,
  confirmLabel = "Remove",
  disabled = false,
  ...rest
}: HeaderActionProps) {
  const confirm = useConfirm();
  // A question is what makes it destructive; the detail is optional.
  const guarded = Boolean(confirmationTitle);

  const activate = (): void => {
    if (!guarded) {
      onClick();
      return;
    }
    confirm({
      title: confirmationTitle ?? "",
      body: confirmationMessage,
      confirmLabel,
      tone: "danger",
      ...(testId ? { "data-testid": `${testId}-confirmation` } : {}),
    })
      .then((confirmed) => {
        if (confirmed) {
          onClick();
        }
      })
      .catch(() => {
        // The provider settles rather than rejects; nothing to recover.
      });
  };

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    activate();
  };

  // Filter data-* attributes from rest props, and pass a reason on only when
  // one was offered, so the button keeps one wrapper throughout.
  const dataProps = Object.fromEntries(
    Object.entries(rest).filter(([key]) => key.startsWith("data-")),
  );
  const reasonProps = "disabledReason" in rest ? { disabledReason: rest.disabledReason } : {};

  return (
    // A real button: it sits beside the header's toggle, not inside it.
    <Button
      type="button"
      size="sm"
      {...variantToButtonProps(variant)}
      disabled={disabled}
      {...reasonProps}
      className={className}
      onClick={handleClick}
      aria-label={ariaLabel ?? label}
      data-testid={testId}
      {...dataProps}
    >
      {icon && <Icon name={icon} className={label ? "mr-1" : ""} aria-hidden="true" />}
      {label}
    </Button>
  );
}

export default AccordionItem;
